/**
* Turns the converted Google photos into models.Photo : thumb, average color, staging, album.
*
* The thumb is drawn from the copy on our own upload server. Asking Google for a smaller one does not
* work : a Picker baseUrl needs the OAuth token, which is why the token is handed to the upload server
* in the first place, and an <img> answering it gets a 403.
*/
fx.ns("shared.extractors", class GoogleExtractor extends shared.MediaExtractor {

    constructor(parameters) {
        super(parameters);

        // the collection the import screen binds to
        this.received = null;

        this._extracting = false;
    }

    // between two batches the queue is empty but the work is not over
    get processing() {
        return this._extracting;
    }

    // once per converted batch : queue behind the running loop rather than starting a second one
    process(files) {
        this._queueFiles = this._queueFiles.concat(files);

        this.processStarting();

        if (!this._extracting)
            this._processQueue();
    }

    // forget what is not extracted, and detach the image in flight — that cancels its download and
    // keeps it out of the album, where it would land after the customer validated or quit
    clear() {
        this._queueFiles = [];
        this._extracting = false;

        if (this._image) {
            this._image.onload = this._image.onerror = null;
            this._image.src = "";
            this._image = null;
        }
    }

    _processQueue() {
        if (this._queueFiles.length > 0) {
            this._extracting = true;

            let file = this._queueFiles.shift();
            this.currentFile = file;

            this._load(file);
        }
        else {
            this._extracting = false;

            this.trigger("photo-extracted-finished");
        }
    }

    _load(file) {
        let image = new Image();

        file.image = image;
        this._image = image;

        image.setAttribute("crossorigin", "anonymous"); // added in order to avoid => "Failed to execute 'toDataURL' on 'HTMLCanvasElement': Tainted canvases may not be exported."
        image.onload = () => this._createPhoto(file);
        image.onerror = () => this._onError(file);

        image.src = file.server_url;
    }

    // a dead url used to stop the import : the queue only ever restarted from the load handler
    _onError(file) {
        file.image = null;

        this._processQueue();
    }

    _createPhoto(file) {
        // the same file a second time : its image was dropped once used
        if (!file || !file.image) {
            this._processQueue();
            return;
        }

        let done;

        try {
            done = this._buildPhoto(file);
        }
        catch (e) {
            // one bad photo used to take the whole import down : the queue only ever restarted from
            // here, so everything behind it was lost and the screen waited for ever (Asana #136)
            fx.log.error(e);
            done = true;
        }

        if (!done)
            return;     // a full-size retry took the file over

        file.image = null;

        this._processQueue();
    }

    // builds the photo, stages it and announces it ; false when it asked for a full-size retry instead
    _buildPhoto(file) {
        // a refused url can still fire onload, and the picture then has no size : the ratio below
        // divides by zero and the canvas comes out full of noise. Skip it rather than stage a broken
        // tile — what is not displayed is not imported either, since the screen and the import both
        // read the photos staged here.
        if (!file.image.naturalWidth || !file.image.naturalHeight)
            return true;

        let date = new Date();

        let newWidth1 = this.thumbSize;
        let newHeight1 = newWidth1 * file.image.height / file.image.width;
        let dataImg = fx.graphics.imageToDataUri(file.image, newWidth1, newHeight1);

        // empty means a tainted canvas. getImageLuminance below reads the pixels back and would throw
        // on it, so the photo is skipped rather than taking the import down with it.
        if (!dataImg)
            return true;

        let luminance = fx.graphics.getImageLuminance(file.image, newWidth1, newHeight1);
        let dateTaken = new Date(file.createTime ? file.createTime : "");

        let photo = new models.Photo({
            creation_id: this.data.creation ? this.data.creation.id : 0,
            accountid: this.data.creation ? this.data.creation.accountid : 0,
            thumb: dataImg,
            light_url : file.server_url,
            width: file.metaData.width,
            height: file.metaData.height,
            orientation: 0,
            date_SQL: date.toSQL(),
            date_unix: date.getUnixTime(),
            date_takenSQL: dateTaken.toSQL(),
            date_taken_unix: dateTaken.getUnixTime(),
            number: 0,
            average_color: luminance
        });

        photo.prop("thumbWidth", newWidth1);
        photo.prop("thumbHeight", newHeight1);
        photo.prop("social", "google");
        photo.prop("media_url", file.server_url);    // the full-size copy : what the upload server re-downloads
        photo.prop("base64", photo.thumb);
        photo.prop("date", date);
        photo.prop("date_taken", dateTaken);

        // stage first, album last : the last addItem wins the parentModel, and photo.delete() must bubble
        if (this.received)
            this.received.addItem(photo);

        this.data.photos.addItem(photo);

        this.trigger("photo-extracted", photo);

        return true;
    }
});
